import { useMemo } from 'react';
import { Card, CardBody, CardFooter, DropdownMenuCard } from 'components/Card';
import dayjs, { type Dayjs } from 'dayjs';
import intl from 'i18n/intl';
import _ from 'lodash';
import { parsedCustomerSignatureSelector } from 'slices/auth.slice';
import { periodsSelector } from 'slices/period.slice';
import { useAppSelector } from 'slices/store';
import { ETimeIntervals } from '@timeedit/types/lib/enums';
import type { TPeriod } from '@timeedit/types/lib/types';
import { Icon, TEDrawerSection, TEInput } from '@timeedit/ui-components';
import {
  DatePicker,
  TimePicker,
  Typography,
} from '@timeedit/ui-components/antd';
import './intervals.scss';

const language = intl.messages as Record<string, string>;

const optionsInterval = Object.values(ETimeIntervals)
  .filter((i) => i !== ETimeIntervals.NONE)
  .sort();
const MappingIntervalTitle = {
  [ETimeIntervals.TIME]: 'Time',
  [ETimeIntervals.DATE]: 'Date',
  [ETimeIntervals.WEEK]: 'Week',
  [ETimeIntervals.WEEK_NUMBER]: 'Week Number',
  [ETimeIntervals.MONTH]: 'Month',
  [ETimeIntervals.YEAR]: 'Year',
  [ETimeIntervals.DAY]: 'Day',
  [ETimeIntervals.WEEKDAY]: 'Weekday',
};

export interface TInfoProps {
  data: TPeriod;
  onChange: (key: string, value: any) => void;
}

const PaddingRuleInfo = ({ data, onChange }: TInfoProps) => {
  const intervals: any = _.get(data, 'intervals', []);
  const childPeriods: any = _.get(data, 'childPeriods', []);
  const periodsData = useAppSelector(periodsSelector);
  const customerSignature = useAppSelector(parsedCustomerSignatureSelector);

  return (
    <TEDrawerSection label={language.intervals_and_related_periods}>
      <div className="section-col">
        <Card
          title="Intervals"
          tooltip="Intervals"
          footer={
            <CardFooter.DropdownMenu
              menu={{
                items: optionsInterval.map((type: string) => ({
                  key: type,
                  label: _.get(MappingIntervalTitle, type, ''),
                })),
                onClick: (option) =>
                  onChange('intervals', [...intervals, { type: option.key }]),
              }}
            />
          }
        >
          <CardBody.VirtualList
            data={intervals.map((interval: any) => String(interval.type))}
          >
            {(item: string, index: number) => (
              <RenderItem
                index={index}
                intervals={intervals}
                onChange={onChange}
                onDelete={() =>
                  onChange(
                    'intervals',
                    intervals.filter((_: any, i: number) => i !== index)
                  )
                }
              />
            )}
          </CardBody.VirtualList>
        </Card>
        <DropdownMenuCard
          title="Child Periods"
          options={periodsData.map((period: TPeriod) => ({
            key: String(period.id),
            label: period.name,
            url: `/${customerSignature}/configuration/periods?item1=${period.id}`,
          }))}
          selectedKeys={childPeriods.map((type: any) => String(type))}
          onChange={(values: string[]) => {
            onChange('childPeriods', values.map(Number));
          }}
        />
      </div>
    </TEDrawerSection>
  );
};

export default PaddingRuleInfo;

const RenderItem = ({ onChange, intervals, index, onDelete }: any) => {
  const detailItem = intervals[index] || {};
  const changeDate = (value: any) => {
    const newIntervals: any = [...intervals];
    if (newIntervals && newIntervals[index]) {
      newIntervals[index] = {
        ...newIntervals[index],
        ...value,
      };
    }
    onChange('intervals', newIntervals);
  };
  return (
    <div className="interval-item">
      <Typography.Text ellipsis>
        {_.get(MappingIntervalTitle, detailItem?.type, '')}
      </Typography.Text>
      <div className="input-container">
        <PickerTypes details={detailItem} onChange={changeDate} />
        {onDelete && (
          <Icon id="DeleteOutlined" alt="Delete" onClick={onDelete} />
        )}
      </div>
    </div>
  );
};

const PickerTypes = ({ onChange, details }: any) => {
  const { type, start, end } = details;

  const onChangeNumber = (type: string, value: number) => {
    onChange({
      [type]: value,
    });
  };
  const onChangeTime = (value: any) => {
    if (!value || !value[0] || !value[1]) return;
    const [start, end] = value;
    if (type === ETimeIntervals.TIME) {
      onChange({
        start: convertTimeToNumber(start.format('HH:mm')),
        end: convertTimeToNumber(end.format('HH:mm')),
      });
    } else if (type === ETimeIntervals.DATE) {
      onChange({
        start: dayjs.utc(start.format('YYYY-MM-DD')).unix(),
        end: dayjs.utc(end.format('YYYY-MM-DD')).unix(),
      });
    } else if (type === ETimeIntervals.WEEK_NUMBER) {
      onChange({
        start: convertWeekToNumber(start),
        end: convertWeekToNumber(end),
      });
    } else if (type === ETimeIntervals.YEAR) {
      onChange({
        start: +start.format('YYYY'),
        end: +end.format('YYYY'),
      });
    }
  };

  const value: any = useMemo(() => {
    if (!start || !end) return undefined;
    if (type === ETimeIntervals.TIME) {
      return [
        convertNumberToTime(start, 'HH:mm'),
        convertNumberToTime(end, 'HH:mm'),
      ];
    } else if (type === ETimeIntervals.DATE) {
      return [dayjs.unix(start).tz('UTC'), dayjs.unix(end).tz('UTC')];
    } else if (type === ETimeIntervals.WEEK_NUMBER) {
      return [convertNumberToWeekDate(start), convertNumberToWeekDate(end)];
    } else if (type === ETimeIntervals.YEAR) {
      return [dayjs('' + start, 'YYYY'), dayjs('' + end, 'YYYY')];
    }
    return undefined;
  }, [start, end, type]);

  switch (type) {
    case ETimeIntervals.DATE:
      return (
        <DatePicker.RangePicker
          size="small"
          value={value}
          format={(date) => date.tz('UTC', true).format('YYYY-MM-DD')}
          onChange={onChangeTime}
        />
      );
    case ETimeIntervals.YEAR:
      return (
        <DatePicker.RangePicker
          size="small"
          value={value}
          onChange={onChangeTime}
          picker="year"
        />
      );
    case ETimeIntervals.WEEK:
      return (
        <NumberRange
          options={weekOps}
          start={start}
          end={end}
          onChangeNumber={onChangeNumber}
        />
      );
    case ETimeIntervals.WEEK_NUMBER:
      return (
        <DatePicker.RangePicker
          size="small"
          value={value}
          onChange={onChangeTime}
          picker="week"
          format={(date) => `${date.weekYear()}-${date.format('wo')}`}
        />
      );
    case ETimeIntervals.MONTH:
      return (
        <NumberRange
          options={monthsOps}
          start={start}
          end={end}
          onChangeNumber={onChangeNumber}
        />
      );
    case ETimeIntervals.DAY:
      return (
        <NumberRange
          options={dayOps}
          start={start}
          end={end}
          onChangeNumber={onChangeNumber}
        />
      );
    case ETimeIntervals.WEEKDAY:
      return (
        <NumberRange
          options={weekdayOps}
          start={start}
          end={end}
          onChangeNumber={onChangeNumber}
        />
      );
    case ETimeIntervals.TIME:
      return (
        <TimePicker.RangePicker
          size="small"
          value={value}
          onChange={onChangeTime}
          format={'HH:mm'}
        />
      );
    default:
      return null;
  }
};

const NumberRange = ({
  options,
  start,
  end,
  onChangeNumber,
}: {
  options: any[];
  start: number;
  end: number;
  onChangeNumber: any;
}) => {
  return (
    <div className="range-number">
      <TEInput.Select
        value={start}
        options={options}
        style={{ marginRight: 8 }}
        onChange={(val) => onChangeNumber('start', val)}
      />
      <TEInput.Select
        value={end}
        options={options}
        onChange={(val) => onChangeNumber('end', val)}
      />
    </div>
  );
};

const listWeekDay = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const weekdayOps = listWeekDay.map((day, index) => ({
  value: index,
  label: day,
}));
const listDay = new Array(31).fill(0);
const dayOps = listDay.map((_, index) => ({
  value: index + 1,
  label: index + 1,
}));
const listWeek = new Array(53).fill(0);
const weekOps = listWeek.map((_, index) => ({
  label: index + 1,
  value: index + 1,
}));
const listMonths = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];
const monthsOps = listMonths.map((month, index) => ({
  label: month,
  value: index + 1,
}));
const towDigits = (number: number) => {
  return number > 9 ? number : '0' + number;
};
const convertTimeToNumber = (time: string) => {
  const [hour, minute] = time.split(':');
  return +hour * 60 + +minute;
};
const convertNumberToTime = (number: number, format: string) => {
  const minute = number % 60;
  const hour = (number - minute) / 60;
  return dayjs(`${towDigits(hour)}:${towDigits(minute)}`, format);
};

const convertWeekToNumber = (date: Dayjs) => {
  return (date.weekYear() % 100) * 100 + date.week();
};
const convertNumberToWeekDate = (number: number) => {
  const YY = Math.floor(number / 100);
  const week = number % 100;
  // Use Jan 4 as the anchor: it always falls in ISO week 1 of year 20YY (ISO 8601
  // rule: week 1 is the first week containing Jan 4). This avoids the edge case where
  // dayjs().year(N) may land on Jan 1–3, which can belong to the previous ISO week year.
  return dayjs(`20${String(YY).padStart(2, '0')}-01-04`).week(week);
};

export const convertTypeToString = (
  type: ETimeIntervals,
  start: number,
  end: number
) => {
  if (!start || !end) return undefined;
  let result: any[] = [start, end];
  if (type === ETimeIntervals.TIME) {
    result = [
      convertNumberToTime(start, 'HH:mm').format('HH:mm'),
      convertNumberToTime(end, 'HH:mm').format('HH:mm'),
    ];
  } else if (type === ETimeIntervals.DATE) {
    result = [
      dayjs.unix(start).tz('UTC').format('YYYY-MM-DD'),
      dayjs.unix(end).tz('UTC').format('YYYY-MM-DD'),
    ];
  } else if (type === ETimeIntervals.WEEK_NUMBER) {
    result = [
      convertNumberToWeekDate(start).week(),
      convertNumberToWeekDate(end).week(),
    ];
  } else if (type === ETimeIntervals.WEEKDAY) {
    result = [listWeekDay[start], listWeekDay[end]];
  }
  return result.join(' - ');
};
